import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import AdminShell from "../components/AdminShell";
import { Plus, Search, Download, Trash2 } from "lucide-react";
import { CAPITAL_PURPOSE_LABEL } from "../lib/quizFunderPreview";

const QUIZ_BAND_SHORT = { lt_100k: "<100K", "100k_1m": "100K–1M", "1m_5m": "1M–5M", "5m_15m": "5M–15M", gt_15m: "15M+" };
const QUIZ_RAISE_SHORT = { under_1m: "<1M", "1m_5m": "1M–5M", "5m_15m": "5M–15M", "15m_25m": "15M–25M", "25m_50m": "25M–50M", "50m_100m": "50M–100M", "100m_500m": "100M–500M", over_500m: "500M+" };

const STATUS_PILL = {
  snapshot: "bg-amber-500/15 text-amber-400",
  deep_dive: "bg-sky-500/15 text-sky-400",
  verified: "bg-green-500/15 text-green-400",
};

export default function AdminBusinesses() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [stageF, setStageF] = useState("all");
  const [profitF, setProfitF] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const { data } = await api.get("/admin/businesses");
    setRows(data || []);
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const purgeTestCompanies = async () => {
    let preview;
    try {
      const { data } = await api.post("/admin/businesses/bulk-delete-test",
        { dry_run: true });
      preview = data;
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Couldn't preview purge");
      return;
    }
    if (!preview.founders_would_delete) {
      window.alert("No test founder accounts found. Nothing to delete.");
      return;
    }
    const sample = (preview.sample_emails || []).slice(0, 8).join("\n  ");
    const ok = window.prompt(
      `PURGE TEST BUSINESSES\n\n` +
      `Founders to delete: ${preview.founders_would_delete}\n` +
      `Companies to delete: ${preview.companies_would_delete}\n\n` +
      (sample ? `Sample emails:\n  ${sample}\n\n` : "") +
      `This deletes the founder users + their companies + every related ` +
      `record (documents, NDAs, deals, alerts). Cannot be undone.\n\n` +
      `Type DELETE to confirm.`
    );
    if (ok !== "DELETE") return;
    try {
      const { data } = await api.post("/admin/businesses/bulk-delete-test");
      window.alert(
        `Deleted ${data.founders_deleted} founder(s) + ` +
        `${data.companies_deleted} company(ies).`);
      setLoading(true);
      await load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Purge failed");
    }
  };

  const filtered = rows.filter(r => {
    if (stageF !== "all" && (r.revenue_stage || "") !== stageF) return false;
    if (profitF !== "all") {
      const p = r.get_started_quiz?.is_profitable || "";
      if (profitF === "unknown" ? p !== "" : p !== profitF) return false;
    }
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (r.company_name || "").toLowerCase().includes(s)
        || (r.sector || "").toLowerCase().includes(s)
        || (r.country || "").toLowerCase().includes(s);
  });

  return (
    <AdminShell>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Businesses</h1>
          <p className="mt-1 text-sm text-neutral-400">All SMEs you are overseeing on behalf of founders.</p>
        </div>
        <Link to="/admin/businesses/new" data-testid="admin-new-biz-btn"
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#F59E0B] px-4 py-2 text-sm font-bold text-neutral-950 hover:bg-[#EAB308]">
          <Plus className="h-4 w-4"/> New business
        </Link>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          onClick={purgeTestCompanies}
          data-testid="admin-biz-purge-test-btn"
          className="inline-flex items-center gap-1.5 rounded-md border border-red-900/60 bg-red-950/40 px-3 py-1.5 text-xs font-semibold text-red-300 hover:bg-red-900/40 hover:text-red-200"
          title="Delete seeded founders matching test_* / @clinqtest.io / founderN@test.com + their companies"
        >
          <Trash2 className="h-3.5 w-3.5"/> Delete test founder companies
        </button>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <div className="flex min-w-[260px] flex-1 items-center gap-2 rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2">
          <Search className="h-4 w-4 text-neutral-500"/>
          <input data-testid="admin-biz-search" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search by name, sector, country…"
            className="w-full bg-transparent text-sm text-neutral-100 outline-none placeholder:text-neutral-600"/>
        </div>
        <select data-testid="admin-biz-filter-stage" value={stageF} onChange={e => setStageF(e.target.value)}
          className="rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm text-neutral-200 outline-none focus:border-[#F59E0B]">
          <option value="all">Stage: All</option>
          <option value="pre_revenue">Pre-revenue</option>
          <option value="early_revenue">Early revenue</option>
          <option value="growth">Growth</option>
        </select>
        <select data-testid="admin-biz-filter-profit" value={profitF} onChange={e => setProfitF(e.target.value)}
          className="rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm text-neutral-200 outline-none focus:border-[#F59E0B]">
          <option value="all">Profitability: All</option>
          <option value="yes">Profitable</option>
          <option value="no">Not profitable</option>
          <option value="not_sure">Not sure</option>
          <option value="unknown">No quiz data</option>
        </select>
        {(stageF !== "all" || profitF !== "all") && (
          <span className="text-xs text-neutral-500" data-testid="admin-biz-filter-count">
            {filtered.length} match{filtered.length === 1 ? "" : "es"}
          </span>
        )}
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-neutral-800 bg-neutral-900">
        <table className="w-full text-sm" data-testid="admin-biz-table">
          <thead className="border-b border-neutral-800 bg-neutral-950 text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="sticky left-0 z-20 w-[220px] min-w-[220px] bg-neutral-950 px-4 py-3 font-semibold shadow-[6px_0_10px_-6px_rgba(0,0,0,0.85)]">Company</th>
              <th className="px-4 py-3 font-semibold">Sector / Country</th>
              <th className="px-4 py-3 font-semibold">Stage</th>
              <th className="px-4 py-3 font-semibold">Journey</th>
              <th className="px-4 py-3 font-semibold">Quiz profile</th>
              <th className="px-4 py-3 font-semibold">Readiness</th>
              <th className="px-4 py-3 font-semibold">Matches</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="px-4 py-10 text-center text-neutral-500">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-neutral-500">
                No businesses yet. <Link to="/admin/businesses/new" className="text-[#F59E0B] hover:underline">Add one →</Link>
              </td></tr>
            )}
            {filtered.map(r => (
              <tr key={r.company_id} className="group border-b border-neutral-800 hover:bg-neutral-800/50"
                  data-testid={`admin-biz-row-${r.company_id}`}>
                <td className="sticky left-0 z-10 w-[220px] min-w-[220px] max-w-[220px] bg-neutral-900 px-4 py-3 shadow-[6px_0_10px_-6px_rgba(0,0,0,0.85)] group-hover:bg-[#1c1c1c]">
                  <Link to={`/admin/businesses/${r.company_id}`} className="font-semibold text-neutral-100 hover:text-[#F59E0B]">
                    {r.company_name || "(Untitled)"}
                  </Link>
                  {r.contact_name && <p className="text-xs text-neutral-500">Contact: {r.contact_name}</p>}
                </td>
                <td className="px-4 py-3 text-neutral-300">
                  {r.sector || "—"} <span className="text-neutral-600">·</span> {r.country || "—"}
                </td>
                <td className="px-4 py-3 text-neutral-300">{r.stage || "—"}</td>
                <td className="px-4 py-3" data-testid={`admin-biz-journey-cell-${r.company_id}`}>
                  <span className="block text-xs text-neutral-300">
                    {{ pre_revenue: "Pre-revenue", early_revenue: "Early revenue", growth: "Growth" }[r.revenue_stage] || "—"}
                  </span>
                  {r.get_started_quiz?.is_profitable && (
                    <span className={
                      "text-[10px] font-bold uppercase " +
                      (r.get_started_quiz.is_profitable === "yes" ? "text-emerald-400"
                        : r.get_started_quiz.is_profitable === "no" ? "text-red-400" : "text-neutral-500")
                    }>
                      {r.get_started_quiz.is_profitable === "not_sure" ? "profit: unsure" : `profit: ${r.get_started_quiz.is_profitable}`}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3" data-testid={`admin-biz-quiz-cell-${r.company_id}`}>
                  {r.get_started_quiz?.stage ? (
                    <div className="space-y-0.5 text-xs">
                      <p className="font-semibold text-neutral-200">
                        {{ pre_revenue: "Pre-revenue", post_revenue: "Post-revenue" }[r.get_started_quiz.stage] || r.get_started_quiz.stage}
                        {r.get_started_quiz.revenue_band && (
                          <span className="text-neutral-500"> · {QUIZ_BAND_SHORT[r.get_started_quiz.revenue_band] || r.get_started_quiz.revenue_band}/mo</span>
                        )}
                      </p>
                      {r.get_started_quiz.raise_amount_bracket && (
                        <p className="text-neutral-400">Raise: {QUIZ_RAISE_SHORT[r.get_started_quiz.raise_amount_bracket] || r.get_started_quiz.raise_amount_bracket}</p>
                      )}
                      {(r.get_started_quiz.capital_purposes || []).length > 0 && (
                        <p className="text-neutral-400" title={(r.get_started_quiz.capital_purposes || []).map((p) => CAPITAL_PURPOSE_LABEL[p] || p).join(", ")}>
                          {CAPITAL_PURPOSE_LABEL[r.get_started_quiz.capital_purpose_primary || r.get_started_quiz.capital_purposes[0]] || r.get_started_quiz.capital_purposes[0]}
                          {r.get_started_quiz.capital_purposes.length > 1 && <span className="text-neutral-600"> +{r.get_started_quiz.capital_purposes.length - 1}</span>}
                        </p>
                      )}
                    </div>
                  ) : <span className="text-xs text-neutral-600">—</span>}
                </td>
                <td className="px-4 py-3">
                  <span className="font-bold text-[#F59E0B]">{r.readiness_score || 0}</span>
                  <span className="text-neutral-600 text-xs">/100</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    <span className="rounded bg-neutral-800 px-1.5 py-0.5 text-[10px] font-semibold text-neutral-300">
                      {r.match_count || 0} total
                    </span>
                    {Object.entries(r.match_by_type || {}).map(([t, n]) => (
                      <span key={t} className="rounded bg-[#F59E0B]/15 px-1.5 py-0.5 text-[10px] font-semibold text-[#F59E0B]">
                        {n} {t}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={"rounded-full px-2 py-0.5 text-[10px] font-bold uppercase " + (STATUS_PILL[r.status] || "bg-neutral-800 text-neutral-400")}>
                    {r.status || "new"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    {r.readiness_score ? (
                      <button
                        data-testid={`admin-biz-pdf-${r.company_id}`}
                        title="Download CFR Report PDF"
                        onClick={async () => {
                          try {
                            const res = await api.get(`/admin/businesses/${r.company_id}/report.pdf`,
                              { responseType: "blob" });
                            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `${(r.company_name || "clinq").replace(/\s+/g, "-").toLowerCase()}-cfr-report.pdf`;
                            document.body.appendChild(a); a.click(); a.remove();
                            setTimeout(() => URL.revokeObjectURL(url), 5000);
                          } catch (e) {
                            window.alert(e?.response?.data?.detail || "Couldn't download.");
                          }
                        }}
                        className="inline-flex items-center gap-1 rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1 text-[11px] font-semibold text-neutral-300 hover:border-[#F59E0B] hover:text-[#F59E0B]"
                      >
                        <Download className="h-3 w-3"/> PDF
                      </button>
                    ) : (
                      <span className="text-[11px] text-neutral-600">—</span>
                    )}
                    <button
                      data-testid={`admin-biz-delete-${r.company_id}`}
                      title="Force-delete this company + its founder owner"
                      onClick={async () => {
                        const ok = window.confirm(
                          `Force-delete "${r.company_name || r.company_id}"?\n\n` +
                          `This removes the company, its founder owner (if any), ` +
                          `all documents, NDAs, deals and alerts. Cannot be undone.`
                        );
                        if (!ok) return;
                        try {
                          await api.delete(`/admin/businesses/${r.company_id}/force`);
                          await load();
                        } catch (e) {
                          window.alert(e?.response?.data?.detail || "Delete failed");
                        }
                      }}
                      className="inline-flex items-center gap-1 rounded-md border border-red-900/60 bg-red-950/30 px-2 py-1 text-[11px] font-semibold text-red-300 hover:bg-red-900/50"
                    >
                      <Trash2 className="h-3 w-3"/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminShell>
  );
}
